import React, { useEffect, useRef, useState } from 'react';
import {
  PAGE_SIZE,
  useNotificationStore
} from '../../stores/notification.store';
import type { icModel } from 'generated-ts-common';
import {
  decodeEscapedHtml,
  formatDate,
  notificationTypesConfig,
  useInfiniteScroll
} from '../../utils/utils';
import { BlankState } from 'ehs-react';
import { Icon } from 'ehs-react';
import { notifyConfirm, Tool } from 'ehs-react';
import { LoadMask } from 'ehs-react';
import { Button } from 'ehs-react';
import { intl } from 'ehs-react';
import { useViewStateStore } from '../../stores/viewState.store';
import type { ValidIconName, LanguageProperty } from 'ehs-react';
import { PopupMenu, usePopupMenu } from '../PopupMenu/PopupMenu';
import { usePortletStore } from '../../App';
import DOMPurify from 'dompurify';

type Notification = icModel.saf.infrastructure.model.notification.Notification;

export function NotificationList(): JSX.Element {
  const [
    notifications,
    loadNotifications,
    loading,
    additionalNotificationsLoading,
    offset,
    init
  ] = useNotificationStore(store => [
    store.notifications,
    store.loadNotifications,
    store.loading,
    store.additionalNotificationsLoading,
    store.offset,
    store.init
  ]);

  const [open] = useViewStateStore(store => [store.open]);

  useEffect(() => {
    init();
  }, [init]);

  useEffect(() => {
    if (open && notifications.length === 0) {
      loadNotifications(0);
    }
  }, [loadNotifications, open, notifications]);

  const loadMoreNotifications = () => {
    loadNotifications(offset + PAGE_SIZE);
  };

  const lastElementRef = useInfiniteScroll(loadMoreNotifications);

  return (
    <div className="nc-notification-list" data-testid="nc-notification-list">
      <LoadMask masked={loading} data-testid="nc-global-load-mask" />
      {!notifications ||
        (notifications.length === 0 &&
          !loading &&
          !additionalNotificationsLoading && (
            <div className="no-notifications" data-testid="no-notifications">
              <BlankState
                iconName="bell"
                description={intl('notificationCenter.noNotifications')}
                scale="medium"
                buttonText={intl('notificationCenter.noNotifications')}
              />
            </div>
          ))}
      {notifications?.map((notification, index) => {
        return (
          <Notification
            key={notification.notificationId}
            notification={notification}
            ref={index === notifications.length - 1 ? lastElementRef : null}
          />
        );
      })}
      <LoadMask
        className="nc-infinite-scroll-load-mask"
        masked={additionalNotificationsLoading}
      />
    </div>
  );
}

function getNotificationLabel(ttDef: any, notification: Partial<Notification>) {
  return ttDef
    ? intl(ttDef['titleKey'] as LanguageProperty)
    : intl(
        ('view.patient.detail.notifications.custom.' +
          notification?.message?.topicType) as LanguageProperty,
        notification.message?.topicType
      );
}

const Notification = React.forwardRef(function Notification(
  props: { notification: Partial<Notification> },
  ref: React.Ref<HTMLDivElement>
): JSX.Element {
  const [markAsRead] = useNotificationStore(store => [store.markAsRead]);
  const [userRole] = usePortletStore(store => [store.configuration.userRole]);

  const ttDef =
    notificationTypesConfig[props.notification.message?.topicType ?? ''];
  return (
    <div
      className="nc-notification"
      ref={ref}
      data-testid={`nc-notification-${props.notification.notificationId}`}
    >
      {!props.notification.read && <div className="unread-marker" />}
      <div
        className="notification-header"
        data-testid={`notification-header-${props.notification.notificationId}`}
      >
        <Icon
          className="notification-header-icon"
          iconName={(ttDef?.icon as ValidIconName) ?? 'envelope'}
          aria-label={getNotificationLabel(ttDef, props.notification)}
        />
        {getNotificationLabel(ttDef, props.notification)}
      </div>
      <ExpandableText text={props.notification.message?.content ?? ''} />

      <div
        className="notification-footer"
        data-testid={`notification-footer-${props.notification.notificationId}`}
      >
        <div className="notification-date">
          {formatDate(new Date(props.notification.creationDate ?? 0))}
        </div>
        {props.notification.targetUrl && (
          <Button
            className="view-button"
            data-testid={`notification-view-button-${props.notification.notificationId}`}
            variant="tertiary"
            text={intl('view.grid.viewdocument.tooltip')}
            onClick={() => {
              markAsRead(props.notification.notificationId!).then(
                () => (window.location.href = props.notification.targetUrl!)
              );
            }}
          />
        )}
      </div>

      {userRole === 'PHYSICIAN' && (
        <div
          className="notification-menu"
          data-testid={`notification-menu-${props.notification.notificationId}`}
        >
          <PopupMenu
            icon="more"
            arialLabel={intl('notificationCenter.action.options')}
            dataTestId={`notification-options-button-${props.notification.notificationId}`}
          >
            <NotificationMenuOptions notification={props.notification} />
          </PopupMenu>
        </div>
      )}
    </div>
  );
});

function NotificationMenuOptions(props: {
  notification: Partial<Notification>;
}): JSX.Element {
  const [
    markAsRead,
    markAsUnread,
    deleteNotification
  ] = useNotificationStore(store => [
    store.markAsRead,
    store.markAsUnread,
    store.deleteNotification
  ]);

  const { close } = usePopupMenu();

  return (
    <>
      <Tool
        data-testid={`delete-notification-button-${props.notification.notificationId}`}
        className="menu-option"
        icon="trash"
        onClick={() =>
          notifyConfirm({
            message: intl('notificationCenter.confirmation.deleteNotification'),
            onConfirm: () => {
              console.log('delete notification');
              deleteNotification(props.notification);
              close();
            }
          })
        }
        text={intl('common.delete')}
      />
      <Tool
        data-testid={`mark-as-read-button-${props.notification.notificationId}`}
        className="menu-option"
        icon="check"
        onClick={() => {
          markAsRead(props.notification.notificationId!);
          close();
        }}
        text={intl('notificationHook.view.dataview.markRead')}
        disabled={props.notification.read}
      />
      <Tool
        data-testid={`mark-as-unread-button-${props.notification.notificationId}`}
        className="menu-option"
        icon="flag"
        onClick={() => {
          markAsUnread(props.notification.notificationId!);
          close();
        }}
        text={intl('notificationHook.view.dataview.markUnread')}
        disabled={!props.notification.read}
      />
    </>
  );
}

interface ExpandableTextProps {
  text: string;
}

const ExpandableText: React.FC<ExpandableTextProps> = ({ text }) => {
  const [isOpen, setIsOpen] = useState(false);
  const textRef = useRef<HTMLDivElement | null>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    if (textRef.current && !isOpen) {
      setIsOverflowing(
        textRef.current.scrollHeight > textRef.current.clientHeight
      );
    }
  }, [text, isOpen]);

  return (
    <div className="expandable-text">
      <div
        className={`text-content ${isOpen ? 'open' : 'closed'}`}
        ref={textRef}
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(decodeEscapedHtml(text))
        }}
      />
      {(isOverflowing || isOpen) && (
        <Button
          className="expand-text-button"
          variant="gray"
          text=""
          icon={isOpen ? 'arrow-line-up-s' : 'arrow-line-down-s'}
          onClick={() => setIsOpen(!isOpen)}
          aria-label={
            isOpen
              ? intl('view.grid.collapse.tooltip')
              : intl('view.grid.expand.tooltip')
          }
        />
      )}
    </div>
  );
};
